import { Alert, Button, Drawer, message, Popconfirm, Spin, Table, Typography } from 'antd';
import { useScreen } from 'hooks/useScreen';
import { useEffect, useState } from 'react';

import type { ClubPeriod, OutstandingRow } from './types';
import { formatMNT, formatPeriod, queryErrorMessage } from './types';

const { Text } = Typography;

interface SplitPart extends ClubPeriod {
  club_month_id: number;
  full_amount: number;
  discount_amount: number;
  amount: number;
}

interface SplitInfo {
  amount: number;
  parts: SplitPart[];
}

// Splits an unpaid invoice from before monthly billing into one invoice per
// Club month of its period, so it can be paid month by month. The total owed
// stays the same; the old invoice is voided.
export default function SplitInvoiceDrawer({
  invoice,
  onClose,
  onDone,
}: {
  invoice: OutstandingRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const { runQuery } = useScreen();
  const [preview, setPreview] = useState<SplitInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    runQuery<SplitInfo>('club_invoice_split', { invoiceId: invoice.id, preview: true })
      .then(([row]) => setPreview(row ?? null))
      .catch((e) => setError(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [invoice.id, runQuery]);

  const split = () => {
    setSaving(true);
    runQuery('club_invoice_split', { invoiceId: invoice.id })
      .then(() => {
        message.success('Нэхэмжлэлийг сараар хуваалаа.');
        onDone();
      })
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setSaving(false));
  };

  const parts = preview?.parts ?? [];
  return (
    <Drawer
      open
      width={560}
      title="Нэхэмжлэлийг сараар хуваах"
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Болих</Button>
          <Popconfirm
            title="Сараар хуваах уу?"
            description={`Хуучин нэхэмжлэл хүчингүй болж, ${parts.length} сарын нэхэмжлэл үүснэ.`}
            okText="Хуваах"
            cancelText="Болих"
            onConfirm={split}
            disabled={parts.length === 0}
          >
            <Button type="primary" loading={saving} disabled={parts.length === 0}>
              {parts.length} сараар хуваах
            </Button>
          </Popconfirm>
        </div>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Text strong>{invoice.student_name}</Text>
        <br />
        <Text type="secondary">
          {formatPeriod(invoice)} · {formatMNT(invoice.amount)}
        </Text>
      </div>

      {loading ? (
        <Spin />
      ) : error ? (
        <Alert type="error" showIcon message={error} />
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Нийт дүн өөрчлөгдөхгүй"
            description="Дүнг сар бүрийн хамрах хичээллэх өдрөөр хуваана: бүтэн сар ижил дүнтэй. Эцэг эх сар сараар нь төлөх боломжтой болно."
          />
          <Table<SplitPart>
            rowKey="club_month_id"
            size="small"
            pagination={false}
            dataSource={parts}
            columns={[
              { title: 'Хугацаа', render: (_, p) => formatPeriod(p) },
              {
                title: 'Хичээллэх өдөр',
                render: (_, p) =>
                  p.sessions_billed === p.sessions_total
                    ? `${p.sessions_total}`
                    : `${p.sessions_total}-аас ${p.sessions_billed}`,
              },
              {
                title: 'Дүн',
                align: 'right',
                render: (_, p) => <Text strong>{formatMNT(p.amount)}</Text>,
              },
            ]}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <Text strong>Нийт</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <Text strong>{formatMNT(parts.reduce((s, p) => s + p.amount, 0))}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </>
      )}
    </Drawer>
  );
}
